import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

import { Categories } from "../../data/DemoData";
import useClickOutside from "../../hooks/useClickOutside";
import { CategoryDropdownProps } from "./types";

export const CategoriesDropdown: React.FC<CategoryDropdownProps> = ({
  setIsOpen,
  categoriesBtnRef,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;

  const dropdownRef = useClickOutside(
    () => {
      setIsOpen(false);
    },
    true,
    categoriesBtnRef
  );

  const categoryKeyMap: { [key: string]: string } = {
    mostPopular: "most_popular",
    directGameTopUp: "topup_plans",
    gameCards: "gamecard_plans",
    giftCards: "giftcard_plans",
    dataServices: "data",
    airtimeServices: "airtime",
    cableTV: "cable_plans",
    electricityPayment: "electric",
  };

  const handleCategoryClick = (e: React.MouseEvent, title: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsOpen(false);

    const categoryKey = categoryKeyMap[title];
    if (!categoryKey) {
      console.error(`No mapping found for category: ${title}`);
      return;
    }

    const sectionId = `section-${categoryKey}`;

    if (window.location.pathname !== "/") {
      navigate("/", {
        state: { scrollTo: categoryKey },
      });
    } else {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 50);
    }
  };

  return (
    <div
      ref={dropdownRef}
      onMouseDown={(e) => e.stopPropagation()} // Use mousedown instead
      className="max-w-sm rounded-3xl overflow-hidden bg-white dark:bg-[black] shadow-lg p-4 w-[268px] h-[383px] z-50"
    >
      <h3 className="text-xs mb-2 dark:text-white">Categories</h3>
      <div className="space-y-2 overflow-y-auto h-[calc(100%-30px)]">
        {Categories.map((category) => (
          <div
            key={category.id}
            className="flex items-center space-x-3 space-y-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
            onClick={(e) => handleCategoryClick(e, category.title)}
          >
            <div className="flex-shrink-0">
              <img src={category.image} alt="category" className="w-8 h-8" />
            </div>
            <div>
              <h3 className="font-bold dark:text-white text-sm">
                {translate(`categories.${category.title}`)}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {translate(`categories.descriptions.${category.description}`)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
